export type QcFailureStamp = { at: string; sessionId: string };

export type QcFailureTrends = {
  last_7_days: number;
  prior_7_days: number;
  per_day: { day: string; count: number }[];
  failures_per_unit_7d: number | null;
};

function dayKey(value: Date): string {
  return value.toISOString().slice(0, 10);
}

/** Counts for the last 14 UTC days. Units are distinct inspection sessions. */
export function qcFailureTrends(stamps: readonly QcFailureStamp[], now = new Date()): QcFailureTrends {
  const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  const perDay: { day: string; count: number }[] = [];
  for (let offset = 6; offset >= 0; offset -= 1) {
    const day = new Date(today);
    day.setUTCDate(today.getUTCDate() - offset);
    perDay.push({ day: dayKey(day), count: 0 });
  }
  const index = new Map(perDay.map((row, i) => [row.day, i]));
  const priorStart = new Date(today);
  priorStart.setUTCDate(today.getUTCDate() - 13);
  const lastStart = new Date(today);
  lastStart.setUTCDate(today.getUTCDate() - 6);
  let prior = 0;
  const units = new Set<string>();
  for (const stamp of stamps) {
    const at = new Date(stamp.at);
    if (Number.isNaN(at.getTime()) || at < priorStart) continue;
    const key = dayKey(at);
    const slot = index.get(key);
    if (slot != null) {
      perDay[slot].count += 1;
      if (stamp.sessionId) units.add(stamp.sessionId);
    } else if (at < lastStart) {
      prior += 1;
    }
  }
  const last = perDay.reduce((sum, row) => sum + row.count, 0);
  return {
    last_7_days: last,
    prior_7_days: prior,
    per_day: perDay,
    failures_per_unit_7d: units.size > 0 ? Math.round((last / units.size) * 10) / 10 : null,
  };
}

export type QcLeaderStamp = { at: string; group: string; item: string };
export type QcLeaderWindow = "today" | "week" | "month";
export type QcLeaderRow = { label: string; count: number };

export type QcFailureLeaders = {
  modes: QcLeaderRow[];
  groups: QcLeaderRow[];
  hasLeader: boolean;
};

function utcMidnight(now: Date): Date {
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
}

function windowStart(window: QcLeaderWindow, now: Date): Date {
  const today = utcMidnight(now);
  if (window === "today") return today;
  if (window === "month") return new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1));
  const weekday = today.getUTCDay();
  const mondayOffset = weekday === 0 ? 6 : weekday - 1;
  const start = new Date(today);
  start.setUTCDate(today.getUTCDate() - mondayOffset);
  return start;
}

function ranked(counts: Map<string, number>): { rows: QcLeaderRow[]; hasLeader: boolean } {
  const ordered = [...counts.entries()]
    .filter(([, count]) => count > 0)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  if (ordered.length === 0) return { rows: [], hasLeader: false };
  const hasLeader = ordered.length === 1 || ordered[0][1] > ordered[1][1];
  return {
    rows: ordered.slice(0, 3).map(([label, count]) => ({ label, count })),
    hasLeader,
  };
}

/** Top failure items and groups for a calendar window. A tie for first is not a leader. */
export function qcFailureLeaders(
  stamps: readonly QcLeaderStamp[],
  window: QcLeaderWindow,
  now = new Date(),
): QcFailureLeaders {
  const start = windowStart(window, now);
  const end = new Date(utcMidnight(now));
  end.setUTCDate(end.getUTCDate() + 1);
  const modes = new Map<string, number>();
  const groups = new Map<string, number>();
  for (const stamp of stamps) {
    const at = new Date(stamp.at);
    if (Number.isNaN(at.getTime()) || at < start || at >= end) continue;
    const item = stamp.item.trim();
    const group = stamp.group.trim();
    if (item) modes.set(item, (modes.get(item) ?? 0) + 1);
    if (group) groups.set(group, (groups.get(group) ?? 0) + 1);
  }
  const modeRank = ranked(modes);
  const groupRank = ranked(groups);
  return {
    modes: modeRank.rows,
    groups: groupRank.rows,
    hasLeader: modeRank.hasLeader || groupRank.hasLeader,
  };
}
